import type {
  Coding,
  Extension,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  TaskInput,
} from '@medplum/fhirtypes';

/**
 * Helpers for the HL7 FHIR translation extension on primitive string elements.
 *
 * Translations live inline on the primitive's `_`-sibling element (e.g. `_text`, `_display`),
 * with the resource's `language` as the base language (`en` by convention). Used for
 * Questionnaire text (`item.text`, `answerOption.valueCoding.display`, `title`) and for
 * cached `Communication` payload/topic translations.
 *
 * @see https://fhir.hl7.org/fhir/languages.html — Translation Extension (StructureDefinition/translation)
 */
export const FHIR_TRANSLATION_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/translation';

/**
 * Canonical URL of our custom per-item framing extension — a short prompt shown above a question
 * (e.g. PHQ-9's "Over the last 2 weeks…"). Its sub-extensions are locale-keyed `valueString`s
 * (`en`, `es`). Written by the questionnaire seeds (`@liza/medplum-config`) and read by the survey
 * renderers (web `@liza/ui` and `apps/patient-native`) via `itemFramingText` in `@liza/ui-core`.
 */
export const ITEM_FRAMING_URL = 'https://liza.health/fhir/StructureDefinition/questionnaire-item-framing';

/**
 * Canonical URL of our authored-language extension: the language preference of the author of the
 * current canonical value, captured at write time (a preference snapshot, not linguistic
 * detection). Carried on the same `_`-sibling element as the field's translation extensions, so
 * an authored original (the translation extension whose `lang` matches this marker) is
 * distinguishable from a lazily cached read translation. Absence means unknown/legacy — readers
 * must not default it to `en`.
 */
export const AUTHORED_LANGUAGE_EXTENSION_URL = 'https://liza.health/fhir/ext/authored-language';

const TRANSLATION_SUB_URL_LANG = 'lang';
const TRANSLATION_SUB_URL_CONTENT = 'content';

/** FHIR JSON: the `_`-sibling Element carrying extensions for a primitive value. */
export type PrimitiveTranslationElement = {
  extension?: Extension[];
};

/**
 * `@medplum/fhirtypes` does not model the `_`-sibling fields of primitives, so resources that
 * carry inline translations need these intersection types to typecheck literal definitions.
 */
export type CodingWithTranslations = Coding & {
  _display?: PrimitiveTranslationElement;
};

/** A `Task.input[]` entry whose free-text `valueString` (the "Notes"/instructions field) carries
 * the author's original alongside the canonical English. */
export type TaskInputWithTranslations = TaskInput & {
  _valueString?: PrimitiveTranslationElement;
};

export type QuestionnaireItemAnswerOptionWithTranslations = Omit<QuestionnaireItemAnswerOption, 'valueCoding'> & {
  valueCoding?: CodingWithTranslations;
  _valueString?: PrimitiveTranslationElement;
};

export type QuestionnaireItemWithTranslations = Omit<QuestionnaireItem, 'answerOption' | 'item'> & {
  _text?: PrimitiveTranslationElement;
  answerOption?: QuestionnaireItemAnswerOptionWithTranslations[];
  item?: QuestionnaireItemWithTranslations[];
};

export type QuestionnaireWithTranslations = Omit<Questionnaire, 'item'> & {
  _title?: PrimitiveTranslationElement;
  _description?: PrimitiveTranslationElement;
  item?: QuestionnaireItemWithTranslations[];
};

export const buildFhirTranslationExtension = (lang: string, content: string): Extension => ({
  url: FHIR_TRANSLATION_EXTENSION_URL,
  extension: [
    { url: TRANSLATION_SUB_URL_LANG, valueCode: lang },
    { url: TRANSLATION_SUB_URL_CONTENT, valueString: content },
  ],
});

export const buildAuthoredLanguageExtension = (lang: string): Extension => ({
  url: AUTHORED_LANGUAGE_EXTENSION_URL,
  valueCode: lang,
});

/** The authored language recorded on a primitive's `_`-sibling, if any. */
export const findAuthoredLanguage = (element: PrimitiveTranslationElement | undefined): string | undefined =>
  element?.extension?.find((e) => e.url === AUTHORED_LANGUAGE_EXTENSION_URL)?.valueCode;

const getTranslationExtensionLang = (ext: Extension): string | undefined =>
  ext.extension?.find((e) => e.url === TRANSLATION_SUB_URL_LANG)?.valueCode;

/** Returns the translated text for `targetLang` from a primitive's extension list, if present. */
export const findTranslationInExtensionList = (extensions: Extension[], targetLang: string): string | undefined => {
  for (const ext of extensions) {
    if (ext.url !== FHIR_TRANSLATION_EXTENSION_URL) {
      continue;
    }
    if (getTranslationExtensionLang(ext) === targetLang) {
      const content = ext.extension?.find((e) => e.url === TRANSLATION_SUB_URL_CONTENT)?.valueString;
      if (content !== undefined) {
        return content;
      }
    }
  }
  return undefined;
};

/** Returns the extension list with any translation extension for `lang` removed; other extensions are preserved. */
export const translationExtensionsWithoutLang = (extensions: Extension[], lang: string): Extension[] =>
  extensions.filter((ext) => {
    if (ext.url !== FHIR_TRANSLATION_EXTENSION_URL) {
      return true;
    }
    return getTranslationExtensionLang(ext) !== lang;
  });

/**
 * Resolves a localized primitive string: the translation for `locale` from the `_`-sibling
 * element when present, otherwise the base `value` (English by convention — the resource's
 * `language`). Resources without translations degrade gracefully to their base text.
 */
export const resolveLocalizedString = (
  value: string | undefined,
  element: PrimitiveTranslationElement | undefined,
  locale: string,
): string | undefined => findTranslationInExtensionList(element?.extension ?? [], locale) ?? value;
